import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import type { CheckInFormProps, CheckInFormData } from './checkInForm.types';
import {
  checkInFormSchema,
  type CheckInFormSchema,
  FORM_FIELD_CLASSES,
} from './checkInForm.definitions';

export default function CheckInForm({
  keyResultId,
  initialValue,
  targetValue,
  currentValue,
  onSubmit,
  onCancel,
  className,
}: CheckInFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CheckInFormSchema>({
    resolver: zodResolver(checkInFormSchema),
    defaultValues: {
      newValue: currentValue,
      impediments: '',
      notes: '',
      actionsRequired: '',
    },
  });

  const handleFormSubmit = (data: CheckInFormSchema) => {
    onSubmit(data as CheckInFormData);
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className={clsx('bg-white rounded-lg border border-gray-200 p-6', className)}
    >
      <h3 className="text-lg font-semibold text-gray-900 font-montserrat mb-6">
        Registrar Check-in
      </h3>

      {/* Reference Values */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-3 gap-4 text-sm font-montserrat">
          <div>
            <span className="text-gray-600">Inicial:</span>
            <span className="ml-2 font-semibold text-gray-900">
              {initialValue}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Atual:</span>
            <span className="ml-2 font-semibold text-gray-900">
              {currentValue}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Meta:</span>
            <span className="ml-2 font-semibold text-blue-600">
              {targetValue}
            </span>
          </div>
        </div>
      </div>

      {/* New Value */}
      <div className="mb-6">
        <label htmlFor="newValue" className={FORM_FIELD_CLASSES.label}>
          Novo Valor *
        </label>
        <input
          id="newValue"
          type="number"
          step="any"
          className={FORM_FIELD_CLASSES.input}
          {...register('newValue', { valueAsNumber: true })}
          placeholder="Digite o novo valor"
        />
        {errors.newValue && (
          <p className={FORM_FIELD_CLASSES.error}>{errors.newValue.message}</p>
        )}
      </div>

      {/* Impediments */}
      <div className="mb-6">
        <label htmlFor="impediments" className={FORM_FIELD_CLASSES.label}>
          Impedimentos
        </label>
        <textarea
          id="impediments"
          rows={3}
          className={FORM_FIELD_CLASSES.textarea}
          {...register('impediments')}
          placeholder="Descreva os impedimentos ou bloqueios encontrados"
        />
        {errors.impediments && (
          <p className={FORM_FIELD_CLASSES.error}>
            {errors.impediments.message}
          </p>
        )}
      </div>

      {/* Notes */}
      <div className="mb-6">
        <label htmlFor="notes" className={FORM_FIELD_CLASSES.label}>
          Notas
        </label>
        <textarea
          id="notes"
          rows={3}
          className={FORM_FIELD_CLASSES.textarea}
          {...register('notes')}
          placeholder="Adicione observações sobre o progresso"
        />
        {errors.notes && (
          <p className={FORM_FIELD_CLASSES.error}>{errors.notes.message}</p>
        )}
      </div>

      {/* Actions Required */}
      <div className="mb-6">
        <label htmlFor="actionsRequired" className={FORM_FIELD_CLASSES.label}>
          Ações Necessárias
        </label>
        <textarea
          id="actionsRequired"
          rows={3}
          className={FORM_FIELD_CLASSES.textarea}
          {...register('actionsRequired')}
          placeholder="Liste as ações necessárias para avançar"
        />
        {errors.actionsRequired && (
          <p className={FORM_FIELD_CLASSES.error}>
            {errors.actionsRequired.message}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className={clsx(
              FORM_FIELD_CLASSES.button,
              FORM_FIELD_CLASSES.buttonSecondary
            )}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          className={clsx(
            FORM_FIELD_CLASSES.button,
            FORM_FIELD_CLASSES.buttonPrimary
          )}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Salvando...' : 'Salvar Check-in'}
        </button>
      </div>
    </form>
  );
}
